/**
 * Contesto React per l'autenticazione.
 * Espone il token JWT corrente, i claim decodificati, e le azioni login/logout.
 * Tutti i componenti dell'app usano useAuth() per accedere allo stato auth.
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { getToken, setToken, clearToken, subscribeToken } from './tokenStore.js';
import { decodeJwtPayload, isTokenExpired } from './jwtDecode.js';
import { hasRenewableSession, renewSession } from './session.js';
import { forgetEntraUser } from './entra.js';
import { reloadToLogin, wipeBrowserData } from './wipeBrowserData.js';
import type { JwtClaims } from './types.js';

interface AuthContextValue {
  /** Token JWT corrente (null = non autenticato). */
  token: string | null;
  /** Claim estratti dal JWT (null = non autenticato o token invalido). */
  claims: JwtClaims | null;
  /** Esegue il login salvando il token e decodificando i claim. */
  login: (token: string) => void;
  /**
   * Logout ("Esci"): rimuove token e claim, azzera tutti i dati del portale nel browser
   * (wipeBrowserData) e ricarica su /login per scartare anche lo stato in memoria.
   */
  logout: () => void;
  /** True se esiste un token valido in store. */
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function buildInitialState(): { token: string | null; claims: JwtClaims | null } {
  const token = getToken();
  const claims = token ? decodeJwtPayload(token) : null;
  return { token, claims };
}

/** Anticipo (ms) con cui rinnovare l'id_token prima di `exp`. */
const REFRESH_LEAD_MS = 60_000;
/** Limite di setTimeout (2^31-1 ms ≈ 24,8 giorni): oltre, il timer scatterebbe subito. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** Provider che avvolge l'app e rende disponibile il contesto auth. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = buildInitialState();
  const [token, setTokenState] = useState<string | null>(initial.token);
  const [claims, setClaimsState] = useState<JwtClaims | null>(initial.claims);

  const login = useCallback((newToken: string) => {
    setToken(newToken);
    setTokenState(newToken);
    setClaimsState(decodeJwtPayload(newToken));
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setTokenState(null);
    setClaimsState(null);
    void forgetEntraUser()
      .then(() => wipeBrowserData())
      .finally(reloadToLogin);
  }, []);

  // Allinea lo stato React ai cambi del tokenStore fuori da login/logout: refresh OIDC
  // (timer qui sotto o 401 in api/client.ts) e sincronizzazione cross-tab.
  useEffect(
    () =>
      subscribeToken((next) => {
        setTokenState(next);
        setClaimsState(next ? decodeJwtPayload(next) : null);
      }),
    [],
  );

  // Rinnovo proattivo della sessione SSO: REFRESH_LEAD_MS prima di `exp`, e al ritorno sul
  // tab (i timer dei tab in background sono rallentati/sospesi, es. laptop in sleep).
  // Senza refresh token (login manuale) non fa nulla: resta il comportamento precedente.
  const exp = claims?.exp;
  useEffect(() => {
    if (exp === undefined || !hasRenewableSession()) return;

    const refreshIfDue = () => {
      if (exp * 1000 - Date.now() <= REFRESH_LEAD_MS) void renewSession();
    };
    const delay = exp * 1000 - Date.now() - REFRESH_LEAD_MS;
    const timer =
      delay <= MAX_TIMEOUT_MS
        ? setTimeout(() => void renewSession(), Math.max(0, delay))
        : undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfDue();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [exp]);

  const value: AuthContextValue = {
    token,
    claims,
    login,
    logout,
    // Token scaduto ma rinnovabile (es. tab rimasto in background oltre `exp`): resta
    // autenticato, il refresh è già in corso (effetto sopra) o avverrà al primo 401.
    isAuthenticated:
      token !== null && claims !== null && (!isTokenExpired(claims) || hasRenewableSession()),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook per accedere al contesto auth.
 * Deve essere usato all'interno di un AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve essere usato all'interno di <AuthProvider>");
  }
  return ctx;
}
